import React, { ReactElement, useState } from "react";
import { useTheme } from "@material-ui/core";

import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Fade from "@mui/material/Fade";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import ModalCancelScheduling from "../modal/ModalCancelScheduling";
import ModalSchedule from "../modal/ModalSchedule";

import { TypographyCaption } from "../typography";
import { IScheduledLoad, StatusSchedule } from "../../model";
import { KEY, STATUSROADLOAD, STATUSTYPE } from "@utils/enum";
import { useFlags } from "flagsmith/react";
import { DeliveryType } from "@utils/DeliveryType";

interface Props<T> {
  row: T;
  rowIndex: number;
  rescheduleAllowed: boolean;
  handleCancelScheduling: (scheduleGroupID: string) => Promise<void>;
  handleCancelCarCharging: (rowData: T) => Promise<void>;
  handleEditAndCreateCarCharging: (rowData: T) => Promise<void>;
  handleEditDriverAndPlate: (rowData: T) => Promise<void>;
  cancelSchedulingAllowed: boolean;
  scheduleCarCharging: boolean;
  cancelCarCharging: boolean;
  editCarCharging: boolean;
  editDriverAllowed?: boolean;
  showEditDriverInMenu?: boolean;
  status: STATUSROADLOAD;
  documentationPending?: boolean;
  onClickDocumentsPending?: () => void;
}

type MenuOption = {
  allowed?: boolean;
  label: string;
  actionType: "link" | "modal-reschedule" | "modal-cancel";
  onClick?: () => void;
};

const LongMenu = <T,>(props: Props<T>): ReactElement => {
  const {
    row,
    rescheduleAllowed,
    handleCancelScheduling,
    handleCancelCarCharging,
    handleEditAndCreateCarCharging,
    handleEditDriverAndPlate,
    scheduleCarCharging,
    editCarCharging,
    cancelCarCharging,
    cancelSchedulingAllowed,
    editDriverAllowed,
    documentationPending,
    onClickDocumentsPending,
  } = props;

  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openReschedule, setOpenReschedule] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const featureFlagProgrammingTransport = useFlags([
    "user_sidebar_programming_transport",
  ]).user_sidebar_programming_transport.enabled;

  const isFOB = (row as { freight?: string }).freight === DeliveryType.FOB;
  const statusProgramation = row?.[KEY.STATUS_PROGRAMATION];

  const options: MenuOption[] = [];

  switch (statusProgramation) {
    case StatusSchedule.PROGRAMMED:
    case StatusSchedule.PARTIALLY_PROGRAMMED:
      options.push({
        allowed: documentationPending,
        label: "Documentos pendentes",
        actionType: "link",
        onClick: () => onClickDocumentsPending?.(),
      });
    //NOTE: Fall through para adicionar opções comuns a PROGRAMMED/PARTIALLY_PROGRAMMED e PENDING
    case StatusSchedule.PENDING:
      options.push(
        {
          allowed: rescheduleAllowed,
          label: "Reagendar",
          actionType: "modal-reschedule",
        },
        {
          allowed: cancelSchedulingAllowed,
          label: "Cancelar Agendamento",
          actionType: "modal-cancel",
        },
        {
          allowed: editDriverAllowed,
          label: "Editar Motorista/Placa",
          actionType: "link",
          onClick: () => handleEditDriverAndPlate(row),
        }
      );
      break;
  }

  if (featureFlagProgrammingTransport && isFOB) {
    if (statusProgramation === StatusSchedule.PENDING) {
      options.push({
        allowed: scheduleCarCharging,
        label: "Programar Carregamento",
        actionType: "link",
        onClick: () => handleEditAndCreateCarCharging(row),
      });
    } else if (
      statusProgramation === StatusSchedule.PROGRAMMED ||
      statusProgramation === StatusSchedule.PARTIALLY_PROGRAMMED
    ) {
      options.push(
        {
          allowed: editCarCharging,
          label: "Editar Carregamento",
          actionType: "link",
          onClick: () => handleEditAndCreateCarCharging(row),
        },
        {
          allowed: cancelCarCharging,
          label: "Cancelar Programação",
          actionType: "link",
          onClick: () => handleCancelCarCharging(row),
        }
      );
    }
  }

  if (!options.some((opt) => opt.allowed)) {
    return (
      <IconButton disabled sx={{ visibility: "hidden", width: 40, height: 40 }}>
        <MoreVertIcon />
      </IconButton>
    );
  }

  return (
    <div>
      <IconButton
        id="long-button"
        aria-label="Mais opções"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        sx={{ color: theme.palette.primary.main }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
      >
        {options.map((option, index) => {
          if (option.allowed === false) return null;

          return (
            <MenuItem
              key={index}
              onClick={() => {
                handleClose();
                if (option.actionType === "link") option.onClick?.();
                if (option.actionType === "modal-reschedule")
                  setOpenReschedule(true);
                if (option.actionType === "modal-cancel") setOpenCancel(true);
              }}
            >
              <TypographyCaption sx={{ color: theme.palette.text.primary }}>
                {option.label}
              </TypographyCaption>
            </MenuItem>
          );
        })}
      </Menu>
      <ModalSchedule
        roadLoadScheduled={row as IScheduledLoad}
        isDisable={!row?.[KEY.RESCHEDULABLE]}
        hiddenIcon
        openExternal={openReschedule}
        onClose={() => setOpenReschedule(false)}
      />

      <ModalCancelScheduling
        isDisabled={row[KEY.SALES_ORDER]?.some(
          (item: any) => item.status === STATUSTYPE.FATURADO
        )}
        scheduleID={row[KEY.REF]}
        handleCancelScheduling={() =>
          handleCancelScheduling(row[KEY.SCHEDULE_GROUP_ID])
        }
        hiddenIcon
        openExternal={openCancel}
        onClose={() => setOpenCancel(false)}
      />
    </div>
  );
};

export default LongMenu;
